'use client';

import React, { useState, useEffect } from 'react';
import { FaCalculator, FaChartLine, FaUsers, FaMoneyBill, FaClock, FaQuestionCircle } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import Chart from 'react-apexcharts';
import { useTheme } from 'next-themes';

const CLTVCaculator = () => {
  const [averagePurchaseValue, setAveragePurchaseValue] = useState('');
  const [purchaseFrequency, setPurchaseFrequency] = useState('');
  const [averageCustomerLifespan, setAverageCustomerLifespan] = useState('');
  const [result, setResult] = useState(null);
  const [chartData, setChartData] = useState({});
  const { theme } = useTheme();

  const calculateCLTV = () => {
    if (!averagePurchaseValue || !purchaseFrequency || !averageCustomerLifespan) {
      alert('Please fill in all fields');
      return;
    }

    const apv = parseFloat(averagePurchaseValue);
    const pf = parseFloat(purchaseFrequency);
    const acl = parseFloat(averageCustomerLifespan);

    if (isNaN(apv) || isNaN(pf) || isNaN(acl)) {
      alert('Please enter valid numbers');
      return;
    }

    const cltv = apv * pf * acl;
    setResult({
      cltv: cltv,
      monthlyCLTV: cltv / 12,
      yearlyCLTV: cltv
    });

    // Update chart data
    const years = Array.from({ length: acl }, (_, i) => i + 1);
    const yearlyValues = years.map(year => (apv * pf * year).toLocaleString());

    setChartData({
      options: {
        chart: {
          type: 'line',
          animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 800
          }
        },
        theme: {
          mode: theme === 'dark' ? 'dark' : 'light'
        },
        xaxis: {
          categories: years,
          title: {
            text: 'Years'
          }
        },
        yaxis: {
          title: {
            text: 'Value (₹)'
          }
        },
        fill: {
          colors: ['#4CAF50']
        },
        responsive: [{
          breakpoint: 480,
          options: {
            chart: {
              width: 300
            }
          }
        }]
      },
      series: [
        {
          name: 'CLTV Growth',
          data: yearlyValues
        }
      ]
    });
  };

  const features = [
    {
      icon: <FaMoneyBill className="text-blue-500" />,
      title: 'Monetary Value',
      description: 'Calculate the total monetary value of a customer over their lifetime'
    },
    {
      icon: <FaChartLine className="text-green-500" />,
      title: 'Growth Visualization',
      description: 'Visualize how CLTV grows year by year with an interactive chart'
    },
    {
      icon: <FaUsers className="text-purple-500" />,
      title: 'Customer Insights',
      description: 'Gain insights into customer behavior and purchasing patterns'
    },
    {
      icon: <FaClock className="text-yellow-500" />,
      title: 'Time-Based Analysis',
      description: 'Analyze customer value over different time periods'
    }
  ];

  const benefits = [
    {
      title: 'Better Marketing Decisions',
      description: 'Make informed decisions about marketing spend and customer acquisition'
    },
    {
      title: 'Improved Retention Strategies',
      description: 'Develop better customer retention programs based on CLTV'
    },
    {
      title: 'Resource Allocation',
      description: 'Optimize resource allocation for maximum customer value'
    },
    {
      title: 'Business Growth',
      description: 'Drive business growth through data-driven customer strategies'
    }
  ];

  const faqs = [
    {
      question: 'What is Customer Lifetime Value (CLTV)?',
      answer: 'CLTV is a prediction of the net profit attributed to the entire future relationship with a customer. It helps businesses understand the total value a customer brings over their lifetime.'
    },
    {
      question: 'How is CLTV calculated?',
      answer: 'CLTV is calculated using three main factors: Average Purchase Value, Purchase Frequency, and Average Customer Lifespan. The formula is: CLTV = APV × PF × ACL'
    },
    {
      question: 'Why is CLTV important?',
      answer: 'CLTV helps businesses make informed decisions about marketing budgets, customer acquisition costs, and retention strategies. It provides a clear picture of customer value over time.'
    },
    {
      question: 'How can I improve my CLTV?',
      answer: 'To improve CLTV, focus on increasing Average Purchase Value, Purchase Frequency, and Customer Lifespan. Implement loyalty programs, improve customer service, and enhance product offerings.'
    }
  ];

  const howToUse = [
    {
      step: '1',
      title: 'Enter Average Purchase Value',
      description: 'Input the average amount spent by a customer per purchase. This is typically calculated by dividing total sales by number of purchases.',
      example: 'Example: If your total sales are ₹10,000 and you had 100 purchases, your average purchase value would be ₹100'
    },
    {
      step: '2',
      title: 'Enter Purchase Frequency',
      description: 'Input how many times an average customer makes a purchase in a year. This can be calculated by dividing total purchases by number of unique customers.',
      example: 'Example: If you have 1,200 purchases from 100 customers in a year, your purchase frequency would be 12 purchases per year'
    },
    {
      step: '3',
      title: 'Enter Customer Lifespan',
      description: 'Input the average number of years a customer remains active with your business. This can be calculated by analyzing customer retention data.',
      example: 'Example: If your customers typically stay with you for 5 years, enter 5'
    },
    {
      step: '4',
      title: 'Calculate CLTV',
      description: 'Click the "Calculate CLTV" button to see your Customer Lifetime Value. The calculator will show you the total CLTV, monthly CLTV, and yearly CLTV.',
      example: 'Example: For APV = ₹100, PF = 12, ACL = 5 years, your CLTV would be ₹6,000'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 dark:from-gray-900 dark:to-gray-800">
      <Helmet>
        <title>CLTV Calculator - Calculate Customer Lifetime Value</title>
        <meta name="description" content="Calculate your Customer Lifetime Value (CLTV) to understand the total value of your customers over their lifetime." />
        <meta property="og:title" content="CLTV Calculator" />
        <meta property="og:description" content="Calculate your Customer Lifetime Value (CLTV) to understand the total value of your customers over their lifetime." />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://example.com/path-to-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="CLTV Calculator" />
        <meta name="twitter:description" content="Calculate your Customer Lifetime Value (CLTV) to understand the total value of your customers over their lifetime." />
        <meta name="twitter:image" content="https://example.com/path-to-image.jpg" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="calculator-header text-center mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-2xl mx-auto"
          >
            <div className="flex items-center justify-center mb-6">
              <FaCalculator className="text-4xl text-blue-500 dark:text-blue-400" />
              <h2 className="text-2xl font-bold ml-3">CLTV Calculator</h2>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); calculateCLTV(); }} className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center border-2 border-gray-200 dark:border-gray-700 rounded-lg p-2">
                  <FaMoneyBill className="text-blue-500 dark:text-blue-400" />
                  <input
                    type="number"
                    placeholder="Average Purchase Value (₹)"
                    value={averagePurchaseValue}
                    onChange={(e) => setAveragePurchaseValue(e.target.value)}
                    className="ml-3 w-full p-2 outline-none bg-gray-50 dark:bg-gray-900"
                  />
                </div>

                <div className="flex items-center border-2 border-gray-200 dark:border-gray-700 rounded-lg p-2">
                  <FaUsers className="text-green-500 dark:text-green-400" />
                  <input
                    type="number"
                    placeholder="Purchase Frequency (per year)"
                    value={purchaseFrequency}
                    onChange={(e) => setPurchaseFrequency(e.target.value)}
                    className="ml-3 w-full p-2 outline-none bg-gray-50 dark:bg-gray-900"
                  />
                </div>

                <div className="flex items-center border-2 border-gray-200 dark:border-gray-700 rounded-lg p-2">
                  <FaClock className="text-purple-500 dark:text-purple-400" />
                  <input
                    type="number"
                    placeholder="Average Customer Lifespan (years)"
                    value={averageCustomerLifespan}
                    onChange={(e) => setAverageCustomerLifespan(e.target.value)}
                    className="ml-3 w-full p-2 outline-none bg-gray-50 dark:bg-gray-900"
                  />
                </div>
              </div>

              <button
                type="submit"
                className="w-full bg-gradient-to-r from-blue-500 to-green-500 text-blue-800 py-3 rounded-lg hover:from-blue-600 hover:to-green-600 transition duration-300"
              >
                Calculate CLTV
              </button>

              {result && (
                <div className="bg-blue-100 dark:bg-blue-900 p-4 rounded-lg mt-4">
                  <h3 className="font-semibold mb-2">Customer Lifetime Value (CLTV):</h3>
                  <div className="space-y-2">
                    <p className="text-blue-800 dark:text-blue-400 text-lg">₹{result.cltv.toLocaleString()}</p>
                    <p className="text-gray-600 dark:text-gray-400">
                      Monthly CLTV: ₹{(result.monthlyCLTV).toLocaleString()}<br />
                      Yearly CLTV: ₹{(result.yearlyCLTV).toLocaleString()}
                    </p>
                  </div>
                </div>
              )}
            </form>
          </motion.div>
        </div>

        {/* Chart Section */}
        {Object.keys(chartData).length > 0 && (
          <div className="mt-12">
            <h3 className="text-2xl font-bold mb-6">CLTV Growth Over Time</h3>
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8">
              <Chart
                options={chartData.options}
                series={chartData.series}
                type="line"
                width={600}
              />
            </div>
          </div>
        )}

        {/* How to Use Section */}
        <div className="mt-16">
          <h3 className="text-2xl font-bold mb-8">How to Use</h3>
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6">
            <ol className="space-y-6">
              {howToUse.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.2 }}
                  className="border-l-4 border-blue-500 pl-4"
                >
                  <div className="flex items-center mb-2">
                    <span className="text-2xl font-bold text-blue-500 mr-3">{step.step}</span>
                    <h4 className="font-semibold">{step.title}</h4>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 mb-2">{step.description}</p>
                  <p className="text-green-500 dark:text-green-400 italic">{step.example}</p>
                </motion.div>
              ))}
            </ol>
          </div>
        </div>

        {/* Features Section */}
        <div className="mt-16">
          <h3 className="text-2xl font-bold mb-8">Key Features</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 text-center"
              >
                {feature.icon}
                <h4 className="mt-3 font-semibold">{feature.title}</h4>
                <p className="mt-2 text-gray-600 dark:text-gray-400">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Benefits Section */}
        <div className="mt-16">
          <h3 className="text-2xl font-bold mb-8">Benefits</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6"
              >
                <h4 className="font-semibold mb-2">{benefit.title}</h4>
                <p className="text-gray-600 dark:text-gray-400">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-16">
          <h3 className="text-2xl font-bold mb-8">Frequently Asked Questions</h3>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6"
              >
                <div className="flex items-center">
                  <FaQuestionCircle className="text-yellow-500 dark:text-yellow-400 mr-3" />
                  <h4 className="font-semibold">{faq.question}</h4>
                </div>
                <p className="mt-2 text-gray-600 dark:text-gray-400">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CLTVCaculator;
